import { useMemo, type ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useTranslation } from '@govard/i18n-core/react';

interface TabItem {
  path: string;
  labelKey: string;
}

const TABS: readonly TabItem[] = [
  { path: '/dashboard', labelKey: 'crm.nav.dashboard' },
  { path: '/deals', labelKey: 'crm.nav.deals' },
  { path: '/contacts', labelKey: 'crm.nav.contacts' },
  { path: '/templates', labelKey: 'crm.nav.templates' },
  { path: '/hot-leads', labelKey: 'crm.nav.hot_leads' },
  { path: '/settings', labelKey: 'crm.nav.settings' },
  { path: '/memory', labelKey: 'crm.nav.memory' },
] as const;

function isTabActive(pathname: string, tabPath: string): boolean {
  return pathname === tabPath || pathname.startsWith(`${tabPath}/`);
}

export function TabBar(): ReactElement | null {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const active = useMemo(
    () => TABS.filter((tab) => isTabActive(location.pathname, tab.path)).map((tab) => tab.path),
    [location.pathname],
  );

  if (location.pathname === '/login') {
    return null;
  }

  const activeKey = active.length > 0 ? active[0] : undefined;

  return (
    <div
      data-testid="crm-tabbar"
      role="tablist"
      style={{
        backgroundColor: '#f5f5f4', // 'var(--color-bg-secondary, #f5f1eb)'
        borderBottom: '1px solid #e7e5e4',
        padding: '0 16px',
      }}
    >
      <div className="flex gap-2" style={{ minHeight: 38, alignItems: 'center' }}>
        {TABS.map((tab) => (
          <button
            key={tab.path}
            type="button"
            role="tab"
            aria-selected={activeKey === tab.path}
            onClick={() => navigate(tab.path)}
            style={{
              background: 'transparent',
              border: 0,
              borderBottom: activeKey === tab.path ? '2px solid #d97706' : '2px solid transparent',
              color: activeKey === tab.path ? '#d97706' : '#78716c',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: activeKey === tab.path ? 600 : 500,
              padding: '8px 10px',
            }}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>
    </div>
  );
}
